import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { AUTH_RETURN_KEY } from './returnPath';

// Front login page (public SPA route), not Drupal's /user/login.
const LOGIN_PATH = '/login';

function redirectToLogin(returnPath) {
  if (window.location.pathname === LOGIN_PATH) return;
  if (returnPath) {
    try { sessionStorage.setItem(AUTH_RETURN_KEY, returnPath); } catch (_) {}
  }
  window.location.href = LOGIN_PATH;
}

/**
 * Blocks rendering until the user is authenticated.
 *
 * On mount it calls /api/user/info (Drupal session-cookie auth).
 * - 200 → stores user in AuthContext, renders children.
 * - 401/403 → saves current path in sessionStorage, redirects to the front
 *   login page (/login).
 *
 * From /login, a password login navigates straight to the return path, while
 * an SSO login round-trips through Drupal, which lands the SPA on /user/{uid}
 * (not proxied by nginx). AuthGuard then reads sessionStorage and navigates to
 * the originally requested page via React Router.
 */
export default function AuthGuard({ children }) {
  const { user, login } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [checking, setChecking] = useState(!user);

  useEffect(() => {
    if (user) {
      // After login Drupal redirects to /user/{uid} → nginx SPA fallback.
      // Restore the originally requested path from sessionStorage.
      try {
        const returnPath = sessionStorage.getItem(AUTH_RETURN_KEY);
        if (returnPath && returnPath.startsWith('/') && !returnPath.startsWith('//')) {
          sessionStorage.removeItem(AUTH_RETURN_KEY);
          const current = location.pathname + location.search + location.hash;
          if (returnPath !== current) {
            navigate(returnPath, { replace: true });
          }
        } else {
          if (returnPath) sessionStorage.removeItem(AUTH_RETURN_KEY);
          if (/^\/user\/\d+/.test(location.pathname)) {
            navigate('/', { replace: true });
          }
        }
      } catch (_) {}
      return;
    }

    let cancelled = false;

    fetch('/api/user/info', { credentials: 'include' })
      .then((res) => {
        if (cancelled) return;
        if (!res.ok) {
          redirectToLogin(location.pathname + location.search + location.hash);
          return;
        }
        return res.json();
      })
      .then((data) => {
        if (cancelled || !data) return;
        login(data);
        setChecking(false);
      })
      .catch(() => {
        if (!cancelled) {
          redirectToLogin(location.pathname + location.search + location.hash);
        }
      });

    return () => { cancelled = true; };
  }, [user, login, navigate, location.pathname, location.search, location.hash]);

  if (checking && !user) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-stone-50">
        <div className="flex flex-col items-center gap-4">
          <div className="w-8 h-8 border-3 border-stone-300 border-t-blue-600 rounded-full animate-spin" />
          <p className="text-sm text-stone-500">Chargement...</p>
        </div>
      </div>
    );
  }

  return children;
}
